import { useState } from 'react'

import { notify } from '~/features/notifications'

import { apiClient } from '~/shared/api'
import { authStore } from '~/shared/model/auth'

import { Button } from '~/components/Button'
import { Loading } from '~/components/Loading'

type ExportDashboardsProps = {
  projectId: string
}

export function ExportDashboards({ projectId }: ExportDashboardsProps) {
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    try {
      setIsExporting(true)

      // Get auth header from the auth store
      const { keyCloakClient } = authStore.getState()
      if (!keyCloakClient?.token) {
        throw new Error('No authentication token available')
      }

      // Build the full URL
      const baseUrl = import.meta.env.VITE_API_URL
      const exportUrl = apiClient.grafana.getExportDashboardsUrl(projectId)
      const fullUrl = `${baseUrl}${exportUrl}`

      // Fetch the zip file
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${keyCloakClient.token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`)
      }

      // Get the blob
      const blob = await response.blob()

      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('content-disposition')
      let filename = 'grafana-dashboards.zip'

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (fileNameMatch && fileNameMatch[1]) {
          filename = fileNameMatch[1].replace(/['"]/g, '')
        }
      }

      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      notify.success('Dashboards exported successfully')
    } catch (error) {
      console.error('Export failed:', error)
      notify.error('Failed to export dashboards. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button onPress={handleExport} isDisabled={isExporting}>
      {isExporting ? <Loading /> : 'Export Dashboards'}
    </Button>
  )
}
